// Store — React Context wrapping the API. TypeScript port of src/store.jsx.
// All UI reads from useStore(); all writes go through actions that hit api.
//
// Routing is delegated to lib/router.ts (hash-based, same scheme as the
// existing demo).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { api } from "./api";
import { useHashRoute } from "./router";
import {
  getTemplateById,
  loadActiveTemplateId,
  loadTemplates,
  saveActiveTemplateId,
  saveTemplates,
  templateToSystemPrompt,
} from "./templates";
import type {
  ActivityEntry,
  Agent,
  Artifact,
  Briefing,
  BriefingFilter,
  BriefingSummary,
  BriefingTemplate,
  Message,
  ParsedAttachment,
  Receipt,
  ResearchPlan,
  Route,
  StreamEvent,
  StreamEventHandler,
  StreamingState,
  StreamStep,
  Thread,
  ToastState,
  TodoItem,
  UploadInFlight,
  User,
} from "./types";

const initialStreaming: StreamingState = {
  active: false,
  threadId: null,
  skills: [],
  activeSkillIndex: -1,
  partial: "",
  steps: [],
  plan: null,
  receipts: [],
  clarifier: null,
  activity: [],
  todos: [],
};

interface StoreValue {
  // auth state — null while we're probing, then true/false
  authenticated: boolean | null;
  signIn: (
    username: string,
    password: string
  ) => Promise<{ ok: boolean; status: number }>;
  signOut: () => void;
  // briefing templates
  templates: BriefingTemplate[];
  activeTemplateId: string;
  activeTemplate: BriefingTemplate | undefined;
  setActiveTemplateId: (id: string) => void;
  upsertTemplate: (template: BriefingTemplate) => void;
  deleteTemplate: (id: string) => void;
  // demo controls — toggle off the brand knowledge graph during demos
  // where the graph isn't populated for the tenant. When false, the
  // agent doesn't get graph_query / graph_schema in its allowed_tools
  // (server-side enforcement) so it can't waste turns hitting an
  // empty graph. Persisted to localStorage so it survives reload.
  useBrandGraph: boolean;
  setUseBrandGraph: (next: boolean) => void;
  // identity + routing
  me: User | null;
  route: Route;
  navigate: (to: string) => void;
  // collections
  threads: Thread[];
  agents: Agent[];
  briefings: BriefingSummary[];
  briefingsById: Record<string, Briefing>;
  briefingFilter: BriefingFilter;
  // current-route derived
  currentThread: Thread | null;
  currentMessages: Message[];
  currentArtifacts: Artifact[];
  // transient ui state
  streaming: StreamingState;
  uploadsInFlight: Record<string, UploadInFlight>;
  toast: ToastState | null;
  // actions
  createThread: (opts?: { title?: string; brandId?: string | null }) => Promise<Thread>;
  createBriefing: () => Promise<Briefing>;
  deleteThread: (id: string) => Promise<void>;
  renameThread: (id: string, title: string) => Promise<void>;
  uploadFiles: (files: FileList | File[]) => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  startConversation: (text: string) => Promise<void>;
  sendToAssistant: (
    text: string,
    attachments: ParsedAttachment[],
    options?: { clarifierAnswers?: Record<string, string>; threadId?: string }
  ) => Promise<void>;
  submitClarifier: (
    answers: Record<string, string>,
    originalText: string
  ) => Promise<void>;
  setBriefingFilter: (next: BriefingFilter) => void;
  loadBriefings: () => Promise<void>;
  loadBriefing: (id: string) => Promise<void>;
  transitionBriefing: (
    id: string,
    to: string,
    reason?: string
  ) => Promise<Briefing>;
  patchBriefing: (id: string, patch: Partial<Briefing>) => Promise<Briefing>;
  commentOnBriefing: (id: string, body: string) => Promise<Briefing>;
  createAgent: (payload: {
    cadence?: string;
    title?: string;
    desc?: string;
  }) => Promise<Agent>;
  toggleAgent: (id: string) => Promise<void>;
  deleteAgent: (id: string) => Promise<void>;
  resetAll: () => Promise<void>;
  setToast: (toast: ToastState | null) => void;
}

const StoreContext = createContext<StoreValue | null>(null);

export function useStore(): StoreValue {
  const ctx = useContext(StoreContext);
  if (!ctx) {
    throw new Error("useStore must be used inside <StoreProvider>");
  }
  return ctx;
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const { route, navigate } = useHashRoute();

  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [templates, setTemplates] = useState<BriefingTemplate[]>([]);
  const [activeTemplateId, setActiveTemplateIdState] = useState<string>("");
  // Brand-graph toggle for demos against tenants whose Neo4j is empty.
  // Defaults to true; reads from localStorage so the choice sticks.
  const [useBrandGraph, setUseBrandGraphState] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const raw = window.localStorage.getItem("inku.useBrandGraph");
    return raw === null ? true : raw === "true";
  });
  const setUseBrandGraph = useCallback((next: boolean) => {
    setUseBrandGraphState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        "inku.useBrandGraph",
        next ? "true" : "false"
      );
    }
  }, []);
  const [me, setMe] = useState<User | null>(null);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [messagesByThread, setMessagesByThread] = useState<
    Record<string, Message[]>
  >({});
  const [artifactsByThread, setArtifactsByThread] = useState<
    Record<string, Artifact[]>
  >({});
  const [streaming, setStreaming] = useState<StreamingState>(initialStreaming);
  const [uploadsInFlight, setUploadsInFlight] = useState<
    Record<string, UploadInFlight>
  >({});
  const [toast, setToast] = useState<ToastState | null>(null);
  const [briefings, setBriefings] = useState<BriefingSummary[]>([]);
  const [briefingsById, setBriefingsById] = useState<Record<string, Briefing>>(
    {}
  );
  const [briefingFilter, setBriefingFilter] = useState<BriefingFilter>({
    status: null,
    owner: null,
  });

  // We hold a ref to current threads inside actions because some action
  // closures need the latest list (e.g. deleteThread navigating to a
  // sibling). Using a ref avoids re-creating action callbacks on every
  // thread mutation.
  const threadsRef = useRef(threads);
  threadsRef.current = threads;

  // ── auth probe (runs first; bootstrap waits on it) ───────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await api.authProbe();
      if (cancelled) return;
      setAuthenticated(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── templates (always loaded; auth-independent — they live in
  // localStorage and are workspace config) ─────────────────────────
  useEffect(() => {
    setTemplates(loadTemplates());
    setActiveTemplateIdState(loadActiveTemplateId());
  }, []);

  // ── bootstrap (runs once authenticated) ──────────────────────────
  useEffect(() => {
    if (authenticated !== true) return;
    let cancelled = false;
    (async () => {
      const u = await api.me();
      if (cancelled) return;
      setMe(u);
      const ts = await api.listThreads();
      if (cancelled) return;
      setThreads(ts);
      const ag = await api.listAgents();
      if (cancelled) return;
      setAgents(ag);
    })();
    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  // ── load thread details when route changes to a thread ────────────
  useEffect(() => {
    if (!route.threadId) return;
    if (
      messagesByThread[route.threadId] &&
      artifactsByThread[route.threadId]
    )
      return;
    let cancelled = false;
    (async () => {
      const [msgs, arts] = await Promise.all([
        api.listMessages(route.threadId!),
        api.listArtifacts(route.threadId!),
      ]);
      if (cancelled) return;
      setMessagesByThread((m) => ({ ...m, [route.threadId!]: msgs }));
      setArtifactsByThread((a) => ({ ...a, [route.threadId!]: arts }));
    })();
    return () => {
      cancelled = true;
    };
  }, [route.threadId]);

  // ── briefings list (auto-load on route or filter change) ──────────
  useEffect(() => {
    if (route.name !== "briefings") return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.listBriefings(briefingFilter);
        if (cancelled) return;
        setBriefings(data.items || []);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route.name, briefingFilter.status, briefingFilter.owner]);

  // ── briefing detail ───────────────────────────────────────────────
  useEffect(() => {
    if (!route.briefingId) return;
    let cancelled = false;
    (async () => {
      try {
        const rec = await api.getBriefing(route.briefingId!);
        if (cancelled) return;
        setBriefingsById((map) => ({ ...map, [route.briefingId!]: rec }));
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route.briefingId]);

  // ── toast auto-dismiss ────────────────────────────────────────────
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // ── derived ───────────────────────────────────────────────────────
  const currentThread = route.threadId
    ? threads.find((t) => t.id === route.threadId) ?? null
    : null;
  const currentMessages = route.threadId
    ? messagesByThread[route.threadId] || []
    : [];
  const currentArtifacts = route.threadId
    ? artifactsByThread[route.threadId] || []
    : [];

  // ── actions ───────────────────────────────────────────────────────
  const createThread = useCallback(
    async (opts: { title?: string; brandId?: string | null } = {}) => {
      const t = await api.createThread({ title: "New thread", ...opts });
      setThreads((x) => [t, ...x]);
      navigate(`/t/${t.id}`);
      return t;
    },
    [navigate]
  );

  const createBriefing = useCallback(async () => {
    const t = await api.createThread({ title: "New briefing" });
    setThreads((x) => [t, ...x]);
    const rec = await api.createBriefing({
      thread_id: t.id,
      title: "New briefing",
      actor: (me && (me.email || me.name)) || "system",
    });
    const summary: BriefingSummary = {
      id: rec.id,
      thread_id: rec.thread_id,
      title: rec.title,
      status: rec.status,
      owner: rec.owner,
      assignees: rec.assignees,
      created_at: rec.created_at,
      updated_at: rec.updated_at,
      comment_count: 0,
      subtitle: "",
      creative_approved_by: null,
      business_approved_by: null,
    };
    setBriefings((x) => [summary, ...x]);
    setBriefingsById((map) => ({ ...map, [rec.id]: rec }));
    navigate(`/briefings/${rec.id}`);
    return rec;
  }, [me, navigate]);

  const deleteThread = useCallback(
    async (id: string) => {
      await api.deleteThread(id);
      setThreads((x) => x.filter((t) => t.id !== id));
      setMessagesByThread((m) => {
        const n = { ...m };
        delete n[id];
        return n;
      });
      setArtifactsByThread((a) => {
        const n = { ...a };
        delete n[id];
        return n;
      });
      const remaining = threadsRef.current.filter((t) => t.id !== id);
      navigate(remaining.length ? `/t/${remaining[0].id}` : "/");
    },
    [navigate]
  );

  const renameThread = useCallback(async (id: string, title: string) => {
    const t = await api.renameThread(id, title);
    setThreads((x) => x.map((y) => (y.id === id ? t : y)));
  }, []);

  // sendToAssistant receives the captured threadId via closure. Updates
  // happen via functional setters so they stay correct even if the route
  // changes mid-stream. Accepts an optional explicit threadId (used by
  // startConversation, which navigates AND sends in the same tick — at
  // that point the route.threadId closure is still the previous route).
  const sendToAssistant = useCallback(
    async (
      text: string,
      attachments: ParsedAttachment[],
      options: {
        clarifierAnswers?: Record<string, string>;
        threadId?: string;
      } = {}
    ) => {
      const threadId = options.threadId ?? route.threadId;
      if (!threadId) return;
      setStreaming({ ...initialStreaming, active: true, threadId });

      const onEvent: StreamEventHandler = (ev: StreamEvent) => {
        if (ev.type === "plan") {
          setStreaming((s) => ({ ...s, plan: ev.plan }));
        } else if (ev.type === "step.start") {
          setStreaming((s) => ({
            ...s,
            steps: [
              ...s.steps,
              {
                id: ev.step.id,
                text: "",
                tools: [],
                done: false,
                isFinal: false,
              },
            ],
          }));
        } else if (ev.type === "step.end") {
          setStreaming((s) => ({
            ...s,
            steps: s.steps.map((st) =>
              st.id === ev.step.id
                ? { ...st, done: true, isFinal: !!ev.step.isFinal }
                : st
            ),
          }));
        } else if (ev.type === "step.delta") {
          setStreaming((s) => {
            if (!s.steps.length) return s;
            const last = s.steps.length - 1;
            const updated = [...s.steps];
            updated[last] = {
              ...updated[last],
              text: updated[last].text + (ev.text || ""),
            };
            return { ...s, steps: updated };
          });
        } else if (ev.type === "skill.start") {
          setStreaming((s) => {
            const next: StreamingState = {
              ...s,
              skills: Array.from(new Set([...s.skills, ev.skill])),
              activeSkillIndex: ev.index,
            };
            if (s.steps.length) {
              const last = s.steps.length - 1;
              next.steps = [...s.steps];
              next.steps[last] = {
                ...next.steps[last],
                tools: [
                  ...(next.steps[last].tools || []),
                  { slug: ev.skill, name: ev.skill, description: "" },
                ],
              };
            }
            return next;
          });
        } else if (ev.type === "skill.done") {
          setStreaming((s) => ({ ...s, activeSkillIndex: ev.index + 1 }));
        } else if (ev.type === "receipt") {
          setStreaming((s) => ({
            ...s,
            receipts: [...(s.receipts || []), ev.receipt],
          }));
        } else if (ev.type === "clarifier") {
          setStreaming((s) => ({ ...s, clarifier: ev.clarifier }));
        } else if (ev.type === "content.delta") {
          setStreaming((s) => ({ ...s, partial: ev.text }));
        } else if (ev.type === "todos") {
          setStreaming((s) => ({
            ...s,
            todos: Array.isArray(ev.todos) ? ev.todos : [],
          }));
        } else if (ev.type === "activity") {
          setStreaming((s) => ({
            ...s,
            activity: [...(s.activity || []), ev.entry],
          }));
        } else if (ev.type === "activity.update") {
          setStreaming((s) => ({
            ...s,
            activity: (s.activity || []).map((e) =>
              e.kind === "tool" && e.tool_use_id === ev.entry.tool_use_id
                ? { ...ev.entry }
                : e
            ),
          }));
        } else if (ev.type === "artifact") {
          setArtifactsByThread((a) => ({
            ...a,
            [threadId]: [...(a[threadId] || []), ev.artifact],
          }));
        } else if (ev.type === "artifact.update") {
          setArtifactsByThread((a) => {
            const list = a[threadId] || [];
            return {
              ...a,
              [threadId]: list.map((x) =>
                x.id === ev.artifact.id ? { ...ev.artifact } : x
              ),
            };
          });
        }
        // 'done' is a no-op — the persisted message lands below.
      };

      try {
        // Resolve the active template lazily so the closure doesn't
        // depend on the memoized activeTemplate (which is declared
        // after sendToAssistant in the provider body).
        const tpl = getTemplateById(templates, activeTemplateId);
        const { message } = await api.chat({
          threadId,
          userText: text,
          attachments,
          onEvent,
          templatePrompt: tpl ? templateToSystemPrompt(tpl) : undefined,
          ...options,
        });
        setMessagesByThread((m) => ({
          ...m,
          [threadId]: [...(m[threadId] || []), message],
        }));
      } catch (e) {
        console.error(e);
        setToast({ kind: "error", text: "Something went wrong." });
      } finally {
        setStreaming({ ...initialStreaming });
      }
    },
    [route.threadId, templates, activeTemplateId]
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (!route.threadId || !text.trim()) return;
      const userMsg = await api.appendMessage(route.threadId, {
        role: "user",
        content: text,
      });
      setMessagesByThread((m) => ({
        ...m,
        [route.threadId!]: [...(m[route.threadId!] || []), userMsg],
      }));
      await sendToAssistant(text, []);
    },
    [route.threadId, sendToAssistant]
  );

  // Start a new thread AND fire the first message in one shot — used by
  // the Home page composer and the starting-point cards. Internally:
  // create thread → append user msg locally → navigate → kick streaming
  // with explicit threadId (route.threadId closure is still stale here).
  const startConversation = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const t = await api.createThread({
        title: trimmed.length > 60 ? trimmed.slice(0, 57) + "…" : trimmed,
      });
      setThreads((x) => [t, ...x]);
      const userMsg = await api.appendMessage(t.id, {
        role: "user",
        content: trimmed,
      });
      setMessagesByThread((m) => ({
        ...m,
        [t.id]: [userMsg],
      }));
      navigate(`/t/${t.id}`);
      await sendToAssistant(trimmed, [], { threadId: t.id });
    },
    [navigate, sendToAssistant]
  );

  const uploadFiles = useCallback(
    async (fileList: FileList | File[]) => {
      if (!route.threadId) return;
      const files = Array.from(fileList);
      const parsed: ParsedAttachment[] = [];
      for (const f of files) {
        const tempId = "tmp_" + Math.random().toString(36).slice(2, 8);
        setUploadsInFlight((s) => ({
          ...s,
          [tempId]: { name: f.name, progress: 0.3 },
        }));
        try {
          const result = await api.uploadAndParse(f);
          parsed.push(result);
          setUploadsInFlight((s) => {
            const n = { ...s };
            delete n[tempId];
            return n;
          });
        } catch {
          setUploadsInFlight((s) => {
            const n = { ...s };
            delete n[tempId];
            return n;
          });
          setToast({ kind: "error", text: `Failed to parse ${f.name}` });
        }
      }
      if (!parsed.length) return;
      const userMsg = await api.appendMessage(route.threadId, {
        role: "user",
        content: `Attached ${parsed.length} file${parsed.length > 1 ? "s" : ""}`,
        attachments: parsed,
      });
      setMessagesByThread((m) => ({
        ...m,
        [route.threadId!]: [...(m[route.threadId!] || []), userMsg],
      }));
      await sendToAssistant("", parsed);
    },
    [route.threadId, sendToAssistant]
  );

  const submitClarifier = useCallback(
    async (answers: Record<string, string>, originalText: string) => {
      const threadId = route.threadId;
      if (!threadId) return;
      const summary = Object.entries(answers)
        .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
        .join(" · ");

      // Mark the most recent clarifier message as answered.
      setMessagesByThread((m) => {
        const list = [...(m[threadId] || [])];
        for (let i = list.length - 1; i >= 0; i--) {
          if (list[i].role === "assistant" && list[i].awaitingClarifier) {
            list[i] = {
              ...list[i],
              awaitingClarifier: false,
              clarifierAnswers: answers,
            };
            break;
          }
        }
        return { ...m, [threadId]: list };
      });

      const userMsg = await api.appendMessage(threadId, {
        role: "user",
        content: summary,
        isClarifierReply: true,
      });
      setMessagesByThread((m) => ({
        ...m,
        [threadId]: [...(m[threadId] || []), userMsg],
      }));
      await sendToAssistant(originalText, [], { clarifierAnswers: answers });
    },
    [route.threadId, sendToAssistant]
  );

  const loadBriefings = useCallback(async () => {
    try {
      const data = await api.listBriefings(briefingFilter);
      setBriefings(data.items || []);
    } catch (e) {
      console.error(e);
    }
  }, [briefingFilter]);

  const loadBriefing = useCallback(async (id: string) => {
    try {
      const rec = await api.getBriefing(id);
      setBriefingsById((map) => ({ ...map, [id]: rec }));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const transitionBriefing = useCallback(
    async (id: string, to: string, reason?: string) => {
      const updated = await api.transitionBriefing(id, {
        to,
        actor: (me && (me.email || me.name)) || "system",
        reason,
      });
      setBriefingsById((map) => ({ ...map, [id]: updated }));
      setBriefings((list) =>
        list.map((b) => (b.id === id ? { ...b, status: updated.status } : b))
      );
      return updated;
    },
    [me]
  );

  const patchBriefing = useCallback(
    async (id: string, patch: Partial<Briefing>) => {
      const updated = await api.patchBriefing(id, patch);
      setBriefingsById((map) => ({ ...map, [id]: updated }));
      return updated;
    },
    []
  );

  const commentOnBriefing = useCallback(
    async (id: string, body: string) => {
      const updated = await api.commentBriefing(id, {
        author: (me && (me.email || me.name)) || "anonymous",
        body,
      });
      setBriefingsById((map) => ({ ...map, [id]: updated }));
      setBriefings((list) =>
        list.map((b) =>
          b.id === id
            ? { ...b, comment_count: (b.comment_count || 0) + 1 }
            : b
        )
      );
      return updated;
    },
    [me]
  );

  const createAgent = useCallback(
    async (payload: { cadence?: string; title?: string; desc?: string }) => {
      const agent = await api.createScheduledAgent(payload);
      setAgents((a) => [...a, agent]);
      return agent;
    },
    []
  );

  const toggleAgent = useCallback(async (id: string) => {
    const updated = await api.toggleAgent(id);
    if (!updated) return;
    setAgents((a) => a.map((x) => (x.id === id ? updated : x)));
  }, []);

  const deleteAgent = useCallback(async (id: string) => {
    await api.deleteAgent(id);
    setAgents((a) => a.filter((x) => x.id !== id));
  }, []);

  const resetAll = useCallback(async () => {
    const result = await api.resetAll();
    setToast({
      kind: "ok",
      text: `Reset complete — server cleared ${result.server_cleared} session(s). Reloading…`,
    });
    setTimeout(() => {
      window.location.reload();
    }, 400);
  }, []);

  const signIn = useCallback(
    async (username: string, password: string) => {
      const result = await api.signIn(username, password);
      if (result.ok) setAuthenticated(true);
      return result;
    },
    []
  );

  // ── template actions ─────────────────────────────────────────────
  const setActiveTemplateId = useCallback((id: string) => {
    saveActiveTemplateId(id);
    setActiveTemplateIdState(id);
  }, []);

  const upsertTemplate = useCallback((template: BriefingTemplate) => {
    setTemplates((list) => {
      const next: BriefingTemplate[] = list.some((t) => t.id === template.id)
        ? list.map((t) => (t.id === template.id ? template : t))
        : [...list, template];
      saveTemplates(next);
      return next;
    });
  }, []);

  const deleteTemplate = useCallback(
    (id: string) => {
      setTemplates((list) => {
        const target = list.find((t) => t.id === id);
        if (!target || target.builtIn) return list; // guard built-ins
        const next = list.filter((t) => t.id !== id);
        saveTemplates(next);
        // If the deleted template was active, fall back to the first
        // remaining template (which always includes the built-in default
        // since built-ins can't be deleted).
        if (activeTemplateId === id && next.length > 0) {
          saveActiveTemplateId(next[0].id);
          setActiveTemplateIdState(next[0].id);
        }
        return next;
      });
    },
    [activeTemplateId]
  );

  const signOut = useCallback(() => {
    api.signOut();
    setAuthenticated(false);
    // Clear in-memory state so a subsequent sign-in doesn't show stale data.
    setMe(null);
    setThreads([]);
    setAgents([]);
    setMessagesByThread({});
    setArtifactsByThread({});
    setBriefings([]);
    setBriefingsById({});
  }, []);

  const activeTemplate = useMemo(
    () => getTemplateById(templates, activeTemplateId),
    [templates, activeTemplateId]
  );

  // Memoize context value — prevents children from re-rendering on
  // unrelated state changes (toast, uploads, etc.) when they don't read
  // those fields.
  const value = useMemo<StoreValue>(
    () => ({
      authenticated,
      signIn,
      signOut,
      templates,
      activeTemplateId,
      activeTemplate,
      setActiveTemplateId,
      upsertTemplate,
      deleteTemplate,
      useBrandGraph,
      setUseBrandGraph,
      me,
      route,
      navigate,
      threads,
      agents,
      briefings,
      briefingsById,
      briefingFilter,
      currentThread,
      currentMessages,
      currentArtifacts,
      streaming,
      uploadsInFlight,
      toast,
      createThread,
      createBriefing,
      deleteThread,
      renameThread,
      uploadFiles,
      sendMessage,
      startConversation,
      sendToAssistant,
      submitClarifier,
      setBriefingFilter,
      loadBriefings,
      loadBriefing,
      transitionBriefing,
      patchBriefing,
      commentOnBriefing,
      createAgent,
      toggleAgent,
      deleteAgent,
      resetAll,
      setToast,
    }),
    [
      authenticated,
      signIn,
      signOut,
      templates,
      activeTemplateId,
      activeTemplate,
      setActiveTemplateId,
      upsertTemplate,
      deleteTemplate,
      useBrandGraph,
      setUseBrandGraph,
      me,
      route,
      navigate,
      threads,
      agents,
      briefings,
      briefingsById,
      briefingFilter,
      currentThread,
      currentMessages,
      currentArtifacts,
      streaming,
      uploadsInFlight,
      toast,
      createThread,
      createBriefing,
      deleteThread,
      renameThread,
      uploadFiles,
      sendMessage,
      startConversation,
      sendToAssistant,
      submitClarifier,
      loadBriefings,
      loadBriefing,
      transitionBriefing,
      patchBriefing,
      commentOnBriefing,
      createAgent,
      toggleAgent,
      deleteAgent,
      resetAll,
    ]
  );

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
}
